import React, { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "./AuthContext";
import { useLanguage } from "./LanguageContext";

const LogIn = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const { t } = useLanguage();

  const [form, setForm] = useState({
    email: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setError("");
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.email || !form.password) return setError(t("login.fillAllFields"));
    setLoading(true);
    try {
      await login({ email: form.email, password: form.password });
      navigate("/");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Page>
      <Gradient />
      <Card
        as={motion.div}
        initial={{ opacity: 0, y: 32 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
      >
        <Logo>SWING<Green>MENTOR</Green></Logo>
        <Tagline>{t("login.tagline")}</Tagline>

        <Form onSubmit={handleSubmit} noValidate>
          <Field>
            <Label>{t("login.email")}</Label>
            <Input
              name="email"
              type="email"
              placeholder="you@example.com"
              value={form.email}
              onChange={handleChange}
              autoComplete="email"
              autoFocus
            />
          </Field>

          <Field>
            <Label>{t("login.password")}</Label>
            <Input
              name="password"
              type="password"
              placeholder="Your password"
              value={form.password}
              onChange={handleChange}
              autoComplete="current-password"
            />
            <ForgotLink to="/forgot-password">{t("login.forgotPassword")}</ForgotLink>
          </Field>

          {location.state?.passwordReset && (
            <NoticeBox>{t("login.passwordResetSuccess")}</NoticeBox>
          )}

          <AnimatePresence>
            {error && (
              <ErrorBox
                as={motion.div}
                key="err"
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
              >
                {error}
              </ErrorBox>
            )}
          </AnimatePresence>

          <SubmitBtn type="submit" disabled={loading}>
            {loading ? <Spinner /> : t("login.submitBtn")}
          </SubmitBtn>
        </Form>

        <Footer>
          {t("login.noAccount")}{" "}
          <FooterLink to="/register">{t("login.signUp")}</FooterLink>
        </Footer>
      </Card>
    </Page>
  );
};

const Spinner = () => (
  <motion.span
    style={{ display: "inline-block", width: 18, height: 18, border: "2px solid #000", borderTop: "2px solid transparent", borderRadius: "50%" }}
    animate={{ rotate: 360 }}
    transition={{ repeat: Infinity, duration: 0.7, ease: "linear" }}
  />
);

const Page = styled.div`
  min-height: 100svh;
  background: #f5faf5;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  font-family: 'Montserrat', sans-serif;
  position: relative;
  overflow: hidden;
`;

const Gradient = styled.div`
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse 80% 60% at 50% -10%, rgba(76,175,80,0.1) 0%, transparent 70%);
  pointer-events: none;
`;

const Card = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 420px;
  background: #fff;
  border: 1px solid rgba(0,0,0,0.08);
  border-radius: 12px;
  padding: 2.5rem 2.5rem 2rem;
  box-shadow: 0 4px 24px rgba(0,0,0,0.08);

  @media (max-width: 480px) {
    padding: 2rem 1.25rem 1.5rem;
  }
`;

const Logo = styled.div`
  font-size: 1.4rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  margin-bottom: 0.25rem;
  color: #111;
`;

const Green = styled.span`
  color: #4CAF50;
`;

const Tagline = styled.div`
  font-size: 0.8rem;
  color: rgba(0,0,0,0.4);
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin-bottom: 2rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const Label = styled.label`
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(0,0,0,0.45);
`;

const Input = styled.input`
  background: #f8f8f8;
  border: 1px solid rgba(0,0,0,0.12);
  border-radius: 6px;
  padding: 0.75rem 1rem;
  font-size: 0.9rem;
  font-family: inherit;
  color: #111;
  transition: border-color 0.2s, background 0.2s;
  outline: none;

  &::placeholder { color: rgba(0,0,0,0.3); }

  &:focus {
    border-color: #4CAF50;
    background: #fff;
  }
`;

const ErrorBox = styled.div`
  background: rgba(220,50,50,0.12);
  border: 1px solid rgba(220,50,50,0.35);
  border-radius: 6px;
  padding: 0.65rem 1rem;
  font-size: 0.82rem;
  color: #ff6b6b;
`;

const NoticeBox = styled(ErrorBox)`
  background: rgba(76,175,80,0.1);
  border-color: rgba(76,175,80,0.3);
  color: #2e7d32;
`;

const SubmitBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 100%;
  padding: 0.9rem;
  margin-top: 0.5rem;
  background: #4CAF50;
  color: #000;
  font-family: inherit;
  font-size: 0.9rem;
  font-weight: 700;
  border: 2px solid #4CAF50;
  border-radius: 6px;
  cursor: pointer;
  letter-spacing: 0.04em;
  transition: background 0.2s, color 0.2s;

  &:hover:not(:disabled) {
    background: #388E3C;
    color: #fff;
  }

  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const Footer = styled.div`
  margin-top: 1.5rem;
  text-align: center;
  font-size: 0.82rem;
  color: rgba(0,0,0,0.45);
`;

const FooterLink = styled(Link)`
  color: #4CAF50;
  text-decoration: none;
  font-weight: 600;

  &:hover { text-decoration: underline; }
`;

const ForgotLink = styled(Link)`
  align-self: flex-end;
  color: #4CAF50;
  text-decoration: none;
  font-size: 0.76rem;
  font-weight: 600;

  &:hover { text-decoration: underline; }
`;

export default LogIn;
